import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Empty, Modal, Segmented, Spin, Table, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { Consignment, ConsignmentHistory } from '../../model/consignment';
import { api_consignment } from '../../api/consignment';
import { formatEventTime } from '../../utils/audit-format';
import { MIN_VISIBLE_ROWS, ROW_HEIGHT_SMALL, useTablePagination } from '../common/table-pagination';
import {
    CountProfitMode,
    CountProfitProductRow,
    CountProfitRange,
    CountProfitReport,
    CountProfitSessionRow,
    billMayHaveCountsIn,
    buildCountProfitReport,
    countProfitRange,
    formatCountProfitRange,
    shopToday,
} from './consignment-count-profit';

interface Props {
    open: boolean;
    onClose: () => void;
}

type LoadStatus = 'loading' | 'ok' | 'failed';

/** HISTORY requests in flight at once: enough to be quick, few enough not
 * to flood a shop's connection when "ปีนี้" needs every bill. */
const HISTORY_CONCURRENCY = 4;

const MODE_OPTIONS: { label: string; value: CountProfitMode }[] = [
    { label: 'วันนี้', value: 'today' },
    { label: 'เดือนนี้', value: 'month' },
    { label: 'ปีนี้', value: 'year' },
    { label: 'เลือกช่วงวันที่', value: 'custom' },
];

/** Ten small rows, then the body scrolls: two tables in one window, and
 * neither may push the other (or the page) out of reach at 100 rows. */
const TABLE_BODY_Y = MIN_VISIBLE_ROWS * ROW_HEIGHT_SMALL;

const money = (value: number) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pieces = (value: number) => Number(value || 0).toLocaleString();

/**
 * กำไรจากการตรวจนับสต๊อก. Read-only: it only ever calls LIST and HISTORY.
 *
 * Totals are shown only when every bill that could hold a count in the
 * chosen range loaded. A failed bill would make the total quietly too
 * small, and the shop would read it as the real figure -- so a partial
 * load shows the failure and no numbers.
 */
export const ConsignmentCountProfitModal: React.FC<Props> = ({ open, onClose }) => {
    const [mode, setMode] = useState<CountProfitMode>('today');
    const [customRange, setCustomRange] = useState<CountProfitRange | null>(null);
    const [status, setStatus] = useState<LoadStatus>('loading');
    const [error, setError] = useState<string>('');
    const [report, setReport] = useState<CountProfitReport | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [today, setToday] = useState(() => shopToday());

    // Cached for as long as the window stays open, so moving between
    // วันนี้/เดือนนี้/ปีนี้ does not refetch what is already here. The page
    // mounts this only while it is open, so reopening (or รีเฟรช) starts
    // clean -- which is how a count saved meanwhile shows up.
    const billsRef = useRef<Consignment[] | null>(null);
    const historyRef = useRef<Map<number, ConsignmentHistory[]>>(new Map());
    const requestSeq = useRef(0);

    // "ปีนี้" can run to many products and many counts: both tables page
    // like every other list. The รวม row always shows the whole range.
    const productPagination = useTablePagination();
    const sessionPagination = useTablePagination();

    const range = useMemo(() => countProfitRange(mode, today, customRange), [mode, today, customRange]);

    useEffect(() => {
        if (!open) return;
        const seq = ++requestSeq.current;
        const load = async () => {
            setStatus('loading');
            setError('');
            try {
                if (!billsRef.current) {
                    const res = await api_consignment('LIST', {});
                    if (!Array.isArray(res)) throw new Error('LIST did not return a list');
                    billsRef.current = res;
                }
                const needed = billsRef.current.filter((bill) => billMayHaveCountsIn(bill, range));
                const missing = needed.filter((bill) => !historyRef.current.has(bill.id));
                let failed = 0;
                let next = 0;
                const worker = async () => {
                    while (next < missing.length) {
                        const bill = missing[next++];
                        try {
                            const res = await api_consignment('HISTORY', {}, bill.id);
                            if (!Array.isArray(res)) throw new Error('HISTORY did not return a list');
                            historyRef.current.set(bill.id, res);
                        } catch (e) {
                            failed += 1;
                        }
                    }
                };
                await Promise.all(Array.from({ length: Math.min(HISTORY_CONCURRENCY, missing.length) }, worker));
                if (seq !== requestSeq.current) return;
                if (failed > 0) {
                    setReport(null);
                    setError(`โหลดประวัติการตรวจนับไม่ครบ (${failed} จาก ${missing.length} บิล) จึงยังไม่แสดงยอด เพราะยอดจะน้อยกว่าความจริง กรุณากดลองใหม่`);
                    setStatus('failed');
                    return;
                }
                setReport(buildCountProfitReport(
                    needed.map((bill) => ({ bill, history: historyRef.current.get(bill.id) || [] })),
                    range
                ));
                setStatus('ok');
            } catch (e) {
                if (seq !== requestSeq.current) return;
                setReport(null);
                setError('โหลดรายการฝากขายไม่สำเร็จ จึงยังไม่แสดงยอด กรุณากดลองใหม่');
                setStatus('failed');
            }
        };
        load();
    }, [open, range, reloadToken]);

    const reload = () => {
        billsRef.current = null;
        historyRef.current = new Map();
        setToday(shopToday());
        setReloadToken((t) => t + 1);
    };

    const productColumns = [
        {
            title: 'สินค้า',
            key: 'product',
            render: (_: unknown, r: CountProfitProductRow) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.product_name}</div>
                    {r.product_code && <div className="consignment-cell-sub">{r.product_code}</div>}
                    {r.lot_count > 1 && <div className="consignment-cell-sub">{r.lot_count} ล็อต</div>}
                </div>
            ),
        },
        {
            title: 'ผู้ฝาก',
            key: 'vendor',
            width: 170,
            render: (_: unknown, r: CountProfitProductRow) => (
                <div>
                    <div>{r.vendor_name || '-'}</div>
                    {/* Over a year one product can sit in a dozen bills; listing
                        them all turned one row into eight lines. */}
                    <div className="consignment-cell-sub" data-testid={`count-profit-docs-${r.key}`}>
                        {r.document_nos.length <= 2
                            ? r.document_nos.join(', ')
                            : <Tooltip title={r.document_nos.join(', ')}><span>{r.document_nos.length} บิล</span></Tooltip>}
                    </div>
                </div>
            ),
        },
        {
            title: 'ขายไป',
            key: 'sold',
            width: 110,
            align: 'right' as const,
            render: (_: unknown, r: CountProfitProductRow) => (
                <div>
                    <strong className="consignment-cell-sold">{pieces(r.sold_quantity)}</strong> {r.product_unit_name || 'ชิ้น'}
                    {r.corrected_quantity > 0 && (
                        <div className="consignment-cell-sub" data-testid={`count-profit-corrected-${r.key}`}>
                            หักนับเกินระบบ {pieces(r.corrected_quantity)}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'กำไร/ชิ้น',
            key: 'unit_profit',
            width: 175,
            align: 'right' as const,
            render: (_: unknown, r: CountProfitProductRow) => (
                <div>
                    <div>฿{money(r.unit_profit)}</div>
                    <div className="consignment-cell-sub">ขาย {money(r.selling_price_per_unit)} − ทุน {money(r.owner_cost_per_unit)}</div>
                </div>
            ),
        },
        {
            title: 'ยอดขาย',
            dataIndex: 'sales_amount',
            key: 'sales_amount',
            width: 120,
            align: 'right' as const,
            render: (v: number) => `฿${money(v)}`,
        },
        {
            title: 'จ่ายผู้ฝาก',
            dataIndex: 'owner_payable',
            key: 'owner_payable',
            width: 120,
            align: 'right' as const,
            // <strong>, not <span>: dark mode flattens every bare td span to grey-white.
            render: (v: number) => <strong className="consignment-cell-payable">฿{money(v)}</strong>,
        },
        {
            title: 'กำไร',
            dataIndex: 'shop_profit',
            key: 'shop_profit',
            width: 120,
            align: 'right' as const,
            render: (v: number) => <strong className="consignment-cell-profit">฿{money(v)}</strong>,
        },
    ];

    const sessionColumns = [
        {
            title: 'วัน-เวลาที่นับ',
            dataIndex: 'counted_at',
            key: 'counted_at',
            width: 150,
            render: (v: string) => formatEventTime(v),
        },
        {
            title: 'บิล / ผู้ฝาก',
            key: 'bill',
            render: (_: unknown, r: CountProfitSessionRow) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.document_no}</div>
                    <div className="consignment-cell-sub">{r.vendor_name || '-'}</div>
                </div>
            ),
        },
        {
            title: 'สินค้าที่นับ',
            dataIndex: 'products_counted',
            key: 'products_counted',
            width: 120,
            align: 'right' as const,
            render: (v: number) => `${pieces(v)} รายการ`,
        },
        {
            title: 'ขายไป',
            key: 'sold',
            width: 110,
            align: 'right' as const,
            render: (_: unknown, r: CountProfitSessionRow) => (
                <div>
                    <strong className="consignment-cell-sold">{pieces(r.sold_quantity)}</strong> ชิ้น
                    {r.corrected_quantity > 0 && <div className="consignment-cell-sub">หักนับเกินระบบ {pieces(r.corrected_quantity)}</div>}
                </div>
            ),
        },
        {
            title: 'ยอดขาย',
            dataIndex: 'sales_amount',
            key: 'sales_amount',
            width: 120,
            align: 'right' as const,
            render: (v: number) => `฿${money(v)}`,
        },
        {
            title: 'จ่ายผู้ฝาก',
            dataIndex: 'owner_payable',
            key: 'owner_payable',
            width: 120,
            align: 'right' as const,
            // <strong>, not <span>: dark mode flattens every bare td span to grey-white.
            render: (v: number) => <strong className="consignment-cell-payable">฿{money(v)}</strong>,
        },
        {
            title: 'กำไร',
            dataIndex: 'shop_profit',
            key: 'shop_profit',
            width: 120,
            align: 'right' as const,
            render: (v: number) => <strong className="consignment-cell-profit">฿{money(v)}</strong>,
        },
        {
            title: 'ผู้นับ',
            dataIndex: 'recorded_by',
            key: 'recorded_by',
            width: 130,
        },
    ];

    const totals = report?.totals;

    return (
        <Modal
            open={open}
            title="📊 กำไรจากการตรวจนับสต๊อก"
            onCancel={onClose}
            footer={null}
            width={1100}
            destroyOnClose
            className="consignment-count-profit-modal"
        >
            <div className="consignment-count-profit-toolbar">
                <Segmented
                    options={MODE_OPTIONS}
                    value={mode}
                    onChange={(value) => {
                        const next = value as CountProfitMode;
                        if (next === 'custom' && !customRange) setCustomRange({ start: today, end: today });
                        setMode(next);
                    }}
                    data-testid="count-profit-mode"
                />
                {mode === 'custom' && (
                    <DatePicker.RangePicker
                        format="DD/MM/YYYY"
                        allowClear={false}
                        value={[dayjs(range.start), dayjs(range.end)]}
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                setCustomRange({ start: dates[0].format('YYYY-MM-DD'), end: dates[1].format('YYYY-MM-DD') });
                            }
                        }}
                        placeholder={['วันที่เริ่มต้น', 'วันที่สิ้นสุด']}
                        data-testid="count-profit-range-picker"
                    />
                )}
                <span className="consignment-count-profit-range" data-testid="count-profit-range">
                    {formatCountProfitRange(range)}
                </span>
                <Button onClick={reload} icon={<FontAwesomeIcon icon="rotate-right" />} data-testid="count-profit-reload">
                    รีเฟรช
                </Button>
            </div>

            <div className="consignment-count-profit-note" data-testid="count-profit-note">
                ฝากขายไม่ได้ตัดสต๊อกตอนขาย ระบบรู้ว่าขายไปเท่าไรตอนตรวจนับ
                กำไรของการนับแต่ละครั้งจึงเป็นของที่ขายไปตั้งแต่การนับครั้งก่อนจนถึงวันที่นับ
            </div>

            {status === 'loading' && (
                <div className="consignment-count-profit-loading" data-testid="count-profit-loading">
                    <Spin />
                    <div>กำลังโหลดข้อมูลการตรวจนับ...</div>
                </div>
            )}

            {status === 'failed' && (
                <Alert
                    type="error"
                    showIcon
                    message={error}
                    action={<Button size="small" onClick={reload}>ลองใหม่</Button>}
                    data-testid="count-profit-error"
                />
            )}

            {status === 'ok' && report && totals && (
                report.sessions.length === 0 ? (
                    <Empty description="ไม่มีการตรวจนับสต๊อกในช่วงวันที่นี้" data-testid="count-profit-empty" />
                ) : (
                    <>
                        <div className="consignment-stats-grid consignment-count-profit-cards">
                            <div className="consignment-stat-card profit">
                                <div className="stat-label">กำไรร้าน</div>
                                <div className="stat-value profit" data-testid="count-profit-total-profit">฿{money(totals.shop_profit)}</div>
                                <div className="stat-sub" data-testid="count-profit-total-counts">
                                    จากการนับ {pieces(totals.count_sessions)} ครั้ง · {pieces(totals.bill_count)} บิล
                                </div>
                            </div>
                            <div className="consignment-stat-card">
                                <div className="stat-label">ยอดขาย</div>
                                <div className="stat-value sales" data-testid="count-profit-total-sales">฿{money(totals.sales_amount)}</div>
                                <div className="stat-sub" data-testid="count-profit-total-sold">ขายไป {pieces(totals.sold_quantity)} ชิ้น</div>
                            </div>
                            <div className="consignment-stat-card payable">
                                <div className="stat-label">จ่ายผู้ฝาก</div>
                                <div className="stat-value payable" data-testid="count-profit-total-payable">฿{money(totals.owner_payable)}</div>
                                <div className="stat-sub">ต้นทุนของที่ขายไป</div>
                            </div>
                        </div>

                        {report.unreadable_rows > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message={`มีประวัติการตรวจนับ ${report.unreadable_rows} รายการที่อ่านวันที่ไม่ได้ จึงไม่ได้รวมในยอดนี้`}
                                data-testid="count-profit-unreadable"
                                style={{ marginBottom: 12 }}
                            />
                        )}

                        <h4 className="consignment-count-profit-heading">แยกตามสินค้า</h4>
                        <Table
                            className="consignment-count-profit-table"
                            data-testid="count-profit-products"
                            columns={productColumns}
                            dataSource={report.products}
                            rowKey="key"
                            size="small"
                            bordered
                            pagination={productPagination}
                            scroll={{ x: 1000, y: TABLE_BODY_Y }}
                            locale={{ emptyText: 'นับแล้วแต่ไม่มีสินค้าขายออกในช่วงนี้' }}
                            summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={2}><strong>รวมทั้งช่วงวันที่</strong></Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} align="right"><strong>{pieces(totals.sold_quantity)}</strong></Table.Summary.Cell>
                                        <Table.Summary.Cell index={3} />
                                        <Table.Summary.Cell index={4} align="right"><strong>฿{money(totals.sales_amount)}</strong></Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} align="right"><strong className="consignment-cell-payable">฿{money(totals.owner_payable)}</strong></Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} align="right">
                                            <strong className="consignment-cell-profit" data-testid="count-profit-products-total">฿{money(totals.shop_profit)}</strong>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />

                        <h4 className="consignment-count-profit-heading">แยกตามครั้งที่นับ</h4>
                        <Table
                            className="consignment-count-profit-table"
                            data-testid="count-profit-sessions"
                            columns={sessionColumns}
                            dataSource={report.sessions}
                            rowKey="key"
                            size="small"
                            bordered
                            pagination={sessionPagination}
                            scroll={{ x: 950, y: TABLE_BODY_Y }}
                        />
                    </>
                )
            )}
        </Modal>
    );
};

export default ConsignmentCountProfitModal;
